import { centralEuropeanDayOfYear, climateAirTemperature, snowCoverFromWaterEquivalent, waterEquivalentFromSnowCover, snowDepthFromWaterEquivalent } from "./climate.js";
export * from "./climate.js";
import { WORLD_MAX_COORDINATE, WORLD_MIN_COORDINATE } from "./world-bounds.js";
export const WEATHER_PRESETS = Object.freeze(["CLEAR", "PARTLY_CLOUDY", "OVERCAST", "FOG", "WINDY", "DRIZZLE", "RAIN", "HEAVY_RAIN", "THUNDERSTORM", "SNOW", "HEAVY_SNOW", "HAIL"]);
// coverage, darkness, attenuation, ambient, fog, wind, rain, snow, hail, lightning
const PRESET_VALUES = {
    CLEAR: [0.04, 0.04, 1, 1, 0.00025, 1.5, 0, 0, 0, 0],
    PARTLY_CLOUDY: [0.38, 0.2, 0.86, 1, 0.0006, 3, 0, 0, 0, 0],
    OVERCAST: [0.96, 0.58, 0.25, 0.85, 0.0015, 4, 0, 0, 0, 0],
    FOG: [0.72, 0.32, 0.42, 0.94, 0.028, 0.6, 0, 0, 0, 0],
    WINDY: [0.32, 0.22, 0.85, 1, 0.0008, 15, 0, 0, 0, 0],
    DRIZZLE: [0.8, 0.42, 0.5, 0.87, 0.0035, 3, 0.18, 0, 0, 0],
    RAIN: [0.91, 0.62, 0.28, 0.77, 0.005, 7, 0.5, 0, 0, 0],
    HEAVY_RAIN: [1, 0.82, 0.13, 0.63, 0.008, 12, 0.9, 0, 0, 0],
    THUNDERSTORM: [1, 0.95, 0.08, 0.55, 0.007, 18, 1, 0, 0, 6],
    SNOW: [0.8, 0.35, 0.48, 0.95, 0.004, 3, 0, 0.45, 0, 0],
    HEAVY_SNOW: [0.97, 0.55, 0.22, 0.86, 0.009, 9, 0, 1, 0, 0],
    HAIL: [0.96, 0.75, 0.2, 0.7, 0.004, 11, 0, 0, 0.85, 0],
};
export function weatherPresetParameters(preset) {
    const p = PRESET_VALUES[preset];
    return { cloudCoverage: p[0], cloudDarkness: p[1], sunAttenuation: p[2], ambientMultiplier: p[3], fogDensity: p[4], windSpeed: p[5],
        rainIntensity: p[6], snowfallIntensity: p[7], hailIntensity: p[8], lightningRate: p[9],
        precipitationIntensity: p[6] + p[7] + p[8], windDirectionRadians: Math.PI / 3 };
}
export function defaultWorldEnvironment(epoch) {
    const weather = weatherPresetParameters("CLEAR"), dayOfYearAtAnchor = centralEuropeanDayOfYear(epoch);
    return { version: 2, revision: 0, dayOfYearAtAnchor, worldTimeMinutesAtAnchor: 780, serverEpochAtAnchor: epoch, timeScale: 24,
        weatherMode: "manual", weatherPreset: "CLEAR", transition: { from: weather, to: { ...weather }, serverEpochAtStart: epoch, durationMs: 0 },
        snowCover: 0, snowWaterEquivalentMm: 0, snowDepthCm: 0,
        groundTemperatureC: climateAirTemperature(dayOfYearAtAnchor, 780, weather), groundWetness: 0, surfaceEpochAtAnchor: epoch };
}
export function sampleWorldCalendar(state, epoch) {
    const minutes = (state.dayOfYearAtAnchor - 1) * 1440 + state.worldTimeMinutesAtAnchor + (epoch - state.serverEpochAtAnchor) / 60_000 * state.timeScale;
    const cycle = ((minutes % (365 * 1440)) + 365 * 1440) % (365 * 1440);
    return { dayOfYear: Math.floor(cycle / 1440) + 1, worldTimeMinutes: wrapWorldMinutes(minutes) };
}
export function sampleWorldTime(state, epoch) {
    return wrapWorldMinutes(state.worldTimeMinutesAtAnchor + (epoch - state.serverEpochAtAnchor) / 60_000 * state.timeScale);
}
export function sampleAirTemperature(state, epoch) {
    const calendar = sampleWorldCalendar(state, epoch);
    return climateAirTemperature(calendar.dayOfYear, calendar.worldTimeMinutes, sampleWeather(state, epoch));
}
export function wrapWorldMinutes(minutes) { return ((minutes % 1440) + 1440) % 1440; }
export function sampleWeather(state, epoch) {
    const t = state.transition;
    const linear = t.durationMs === 0 ? 1 : clamp01((epoch - t.serverEpochAtStart) / t.durationMs);
    if (linear === 0)
        return { ...t.from };
    if (linear === 1)
        return { ...t.to };
    const alpha = linear * linear * (3 - 2 * linear);
    const result = {};
    for (const key of WEATHER_KEYS)
        result[key] = t.from[key] + (t.to[key] - t.from[key]) * alpha;
    const delta = Math.atan2(Math.sin(t.to.windDirectionRadians - t.from.windDirectionRadians), Math.cos(t.to.windDirectionRadians - t.from.windDirectionRadians));
    result.windDirectionRadians = ((t.from.windDirectionRadians + delta * alpha) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
    return result;
}
export function isEnvironmentPatch(value) {
    if (!record(value) || Object.keys(value).length === 0 || !keys(value, PATCH_KEYS, false))
        return false;
    return Object.entries(value).every(([key, item]) => {
        if (key === "weatherMode")
            return item === "manual" || item === "auto";
        if (key === "weatherPreset")
            return isWeatherPreset(item);
        if (key === "worldTimeMinutes")
            return bounded(item, 0, 1440) && item !== 1440;
        if (key === "dayOfYear")
            return integer(item, 1, 365);
        if (key === "groundTemperatureC")
            return bounded(item, -40, 50);
        if (key === "snowWaterEquivalentMm")
            return bounded(item, 0, 250);
        if (key === "timeScale")
            return bounded(item, 0, 3600);
        if (key === "transitionSeconds" || key === "lightningRate")
            return bounded(item, 0, 60);
        if (key === "windSpeed")
            return bounded(item, 0, 40);
        if (key === "windDirectionRadians")
            return bounded(item, 0, Math.PI * 2);
        return bounded(item, 0, 1);
    });
}
export function isWeatherPreset(value) { return typeof value === "string" && WEATHER_PRESETS.includes(value); }
export function isWeatherParameters(value) {
    if (!record(value) || !keys(value, WEATHER_KEYS))
        return false;
    return WEATHER_KEYS.every(key => {
        const max = key === "windSpeed" ? 40 : key === "windDirectionRadians" ? Math.PI * 2 : key === "lightningRate" ? 60 : key === "ambientMultiplier" ? 2 : key === "fogDensity" ? 0.05 : 1;
        return bounded(value[key], 0, max);
    }) && Math.abs(value.rainIntensity + value.snowfallIntensity + value.hailIntensity - value.precipitationIntensity) < 0.000001;
}
const LEGACY_STATE_KEYS = ["version", "revision", "worldTimeMinutesAtAnchor", "serverEpochAtAnchor", "timeScale", "weatherMode", "weatherPreset", "transition", "snowCover", "groundWetness", "surfaceEpochAtAnchor"];
function validEnvironmentCommon(value) {
    const t = value.transition;
    return integer(value.revision, 0, Number.MAX_SAFE_INTEGER) && bounded(value.worldTimeMinutesAtAnchor, 0, 1440) && value.worldTimeMinutesAtAnchor !== 1440
        && epoch(value.serverEpochAtAnchor) && epoch(value.surfaceEpochAtAnchor) && bounded(value.timeScale, 0, 3600)
        && (value.weatherMode === "manual" || value.weatherMode === "auto") && isWeatherPreset(value.weatherPreset)
        && bounded(value.snowCover, 0, 1) && bounded(value.groundWetness, 0, 1)
        && record(t) && keys(t, ["from", "to", "serverEpochAtStart", "durationMs"]) && isWeatherParameters(t.from) && isWeatherParameters(t.to)
        && epoch(t.serverEpochAtStart) && bounded(t.durationMs, 0, 60_000);
}
export function isWorldEnvironmentState(value) {
    return record(value) && keys(value, [...LEGACY_STATE_KEYS, "dayOfYearAtAnchor", "groundTemperatureC", "snowWaterEquivalentMm", "snowDepthCm"])
        && value.version === 2 && validEnvironmentCommon(value) && integer(value.dayOfYearAtAnchor, 1, 365)
        && bounded(value.groundTemperatureC, -40, 50) && bounded(value.snowWaterEquivalentMm, 0, 250) && bounded(value.snowDepthCm, 0, 450)
        && Math.abs(value.snowCover - snowCoverFromWaterEquivalent(value.snowWaterEquivalentMm)) < 0.000001;
}
/** Persistence migration only: old wire messages remain invalid. Preserve current
 * clock/weather/population externally; old default 1x upgrades once to 24x. */
export function migrateWorldEnvironment(value, now) {
    if (isWorldEnvironmentState(value))
        return structuredClone(value);
    if (!record(value) || !keys(value, LEGACY_STATE_KEYS) || value.version !== 1 || !validEnvironmentCommon(value))
        return null;
    const legacy = value;
    const dayOfYearAtAnchor = centralEuropeanDayOfYear(now), worldTimeMinutesAtAnchor = sampleWorldTime(legacy, now);
    const weather = sampleWeather(legacy, now);
    const air = climateAirTemperature(dayOfYearAtAnchor, worldTimeMinutesAtAnchor, weather);
    const snowWaterEquivalentMm = waterEquivalentFromSnowCover(legacy.snowCover);
    return { ...legacy, version: 2, dayOfYearAtAnchor, worldTimeMinutesAtAnchor, serverEpochAtAnchor: now, surfaceEpochAtAnchor: now,
        timeScale: legacy.timeScale === 1 ? 24 : legacy.timeScale, snowWaterEquivalentMm,
        snowDepthCm: snowDepthFromWaterEquivalent(snowWaterEquivalentMm, air),
        groundTemperatureC: legacy.snowCover > 0.05 ? Math.min(0, air) : air };
}
export function isLightningEvent(value) {
    return record(value) && keys(value, ["id", "serverTime", "x", "y", "z", "seed", "intensity"])
        && typeof value.id === "string" && /^[a-zA-Z0-9_-]{1,80}$/.test(value.id) && epoch(value.serverTime)
        && bounded(value.x, WORLD_MIN_COORDINATE, WORLD_MAX_COORDINATE) && bounded(value.z, WORLD_MIN_COORDINATE, WORLD_MAX_COORDINATE)
        && bounded(value.y, -2000, 4000) && integer(value.seed, 0, 0xffffffff) && bounded(value.intensity, 0.1, 2);
}
export function isServerEnvironment(value) {
    return record(value) && keys(value, ["t", "state", "serverTime"]) && value.t === "environment" && epoch(value.serverTime) && isWorldEnvironmentState(value.state);
}
export function isServerLightning(value) {
    return record(value) && keys(value, ["t", "event"]) && value.t === "lightning" && isLightningEvent(value.event);
}
export function clamp01(value) { return Math.max(0, Math.min(1, value)); }
const WEATHER_KEYS = ["windDirectionRadians", "windSpeed", "precipitationIntensity", "rainIntensity", "snowfallIntensity", "hailIntensity", "cloudCoverage", "cloudDarkness", "sunAttenuation", "ambientMultiplier", "fogDensity", "lightningRate"];
const PATCH_KEYS = ["worldTimeMinutes", "dayOfYear", "groundTemperatureC", "snowWaterEquivalentMm", "timeScale", "weatherMode", "weatherPreset", "transitionSeconds", "windDirectionRadians", "windSpeed", "precipitationIntensity", "cloudCoverage", "lightningRate", "snowCover", "groundWetness"];
function record(value) { return !!value && typeof value === "object" && !Array.isArray(value); }
function keys(value, allowed, exact = true) { const own = Object.keys(value); return (!exact || own.length === allowed.length) && own.every(key => allowed.includes(key)); }
function bounded(value, min, max) { return typeof value === "number" && Number.isFinite(value) && value >= min && value <= max; }
function integer(value, min, max) { return bounded(value, min, max) && Number.isSafeInteger(value); }
function epoch(value) { return integer(value, 0, 8_640_000_000_000_000); }
